import { FunctionComponent, useCallback, useState } from 'react'
import { IconButton } from '../../../../../shared'
import { DropdownContainer, DropdownItem } from '../../../../../shared/dropdown'
import { DropdownGroup } from '../../../../../shared/dropdown/DropdownGroup'
import {
  useGetPatientInteropUrls,
  useSyncPatientInterop,
} from '../../../../../../hooks/queries/patients'
import { InteropActionsMenuProps, InteropDocumentsAction } from './InteropActionsMenu.model'
import styles from './InteropActionsMenu.module.scss'

const getDocumentsLabel = ({ count, isPending }: InteropDocumentsAction) => {
  if (isPending) {
    return 'Envoi en cours...'
  }

  return count === 0
    ? "Aucun document à envoyer à l'établissement"
    : "Envoyer les documents à l'établissement"
}

export const InteropActionsMenu: FunctionComponent<InteropActionsMenuProps> = ({
  patient,
  documentsAction,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const {
    query: { data: interopUrls = [], isLoading: isInteropUrlsLoading },
  } = useGetPatientInteropUrls({ patientId: patient.id })
  const { mutate: syncPatient, isPending: isSyncingPatient } = useSyncPatientInterop()

  const closeMenu = useCallback(() => setIsOpen(false), [])
  const isDocumentsDisabled =
    !documentsAction || documentsAction.isPending || documentsAction.count === 0

  // The spinner moves to the main button so a pending action stays visible once the menu is closed.
  const isPending = isSyncingPatient || !!documentsAction?.isPending

  return (
    <div className={styles.container}>
      <IconButton
        icon="hospital"
        theme="primary"
        title="Actions interop"
        onClick={() => setIsOpen((value) => !value)}
        isLoading={!isOpen && isPending}
        testId="button-patient_page-header-interop-menu"
      />
      <div className={styles.dropdown}>
        <DropdownContainer opened={isOpen} closeDropdown={closeMenu} verticalScroll={false}>
          <DropdownGroup label="Patient">
            <DropdownItem
              icon="reload"
              layout="thin"
              onClick={isSyncingPatient ? undefined : () => syncPatient({ serial: patient.serial })}
              isDisabled={isSyncingPatient}
              testId="button-patient_page-header-interop-sync"
            >
              {isSyncingPatient ? 'Synchronisation en cours...' : 'Synchroniser le patient'}
            </DropdownItem>
          </DropdownGroup>
          {(isInteropUrlsLoading || interopUrls.length > 0) && (
            <DropdownGroup label="Liens externes">
              {isInteropUrlsLoading ? (
                <DropdownItem layout="thin" isDisabled>
                  Chargement des URLs...
                </DropdownItem>
              ) : (
                interopUrls.map(({ name, url }) => (
                  <DropdownItem
                    key={`${name}-${url}`}
                    icon="externalLink"
                    layout="thin"
                    href={url}
                    target="_blank"
                    testId="button-patient_page-header-interop-url"
                  >
                    {name}
                  </DropdownItem>
                ))
              )}
            </DropdownGroup>
          )}
          {documentsAction && (
            <DropdownGroup label="Documents">
              <DropdownItem
                icon="export"
                layout="thin"
                onClick={isDocumentsDisabled ? undefined : documentsAction.onClick}
                isDisabled={isDocumentsDisabled}
                testId="button-patient_page-header-interop-documents"
              >
                {getDocumentsLabel(documentsAction)}
              </DropdownItem>
            </DropdownGroup>
          )}
        </DropdownContainer>
      </div>
    </div>
  )
}
